import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  amount, 
  serviceName,
  onPaymentSuccess,
  onPaymentInitiate
}) => {
  const [selectedOperator, setSelectedOperator] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'pending', 'success', 'failed'
  const [paymentRef, setPaymentRef] = useState(null);

  const operators = [
    { value: 'orange', label: 'Orange Money', color: 'bg-orange-500' },
    { value: 'airtel', label: 'Airtel Money', color: 'bg-red-600' },
    { value: 'mpesa', label: 'M-Pesa', color: 'bg-green-600' }
  ];

  const handlePayment = async () => {
    if (!selectedOperator || !phoneNumber) {
      toast.error('Veuillez sélectionner un opérateur et entrer votre numéro');
      return;
    }

    setProcessing(true);
    setPaymentStatus('pending');
    
    try {
      // Appeler le callback fourni par le parent qui contient les données spécifiques au service
      const paymentData = await onPaymentInitiate({
        operator: selectedOperator,
        phone_number: phoneNumber,
        amount: amount
      });

      setPaymentRef(paymentData.payment_reference);
      
      toast.success('✅ Paiement initié ! Confirmez sur votre téléphone.');
      
      // Simuler l'attente de confirmation (en prod, on attendrait le webhook)
      // Pour le moment, on considère le paiement comme réussi après 3 secondes
      setTimeout(() => {
        setPaymentStatus('success');
        setTimeout(() => {
          onPaymentSuccess(paymentData);
          onClose();
        }, 1500);
      }, 3000);

    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus('failed');
      toast.error(error.response?.data?.detail || 'Erreur de paiement');
      setProcessing(false);
    }
  };

  const resetModal = () => {
    setSelectedOperator('');
    setPhoneNumber('');
    setProcessing(false);
    setPaymentStatus(null);
    setPaymentRef(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl">Paiement - {serviceName}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Montant à payer : <span className="text-green-500 font-bold text-xl">{amount}$</span>
          </DialogDescription>
        </DialogHeader>

        {paymentStatus === 'success' ? (
          <div className="text-center py-8">
            <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
            <h3 className="text-xl font-bold text-white mb-2">Paiement Réussi ! 🎉</h3>
            <p className="text-zinc-400">Votre demande a été enregistrée.</p>
          </div>
        ) : paymentStatus === 'failed' ? (
          <div className="text-center py-8">
            <XCircle className="mx-auto mb-4 text-red-500" size={64} />
            <h3 className="text-xl font-bold text-white mb-2">Paiement Échoué</h3>
            <p className="text-zinc-400 mb-4">Veuillez réessayer.</p>
            <Button onClick={() => setPaymentStatus(null)} className="bg-[#E50914]">
              Réessayer
            </Button>
          </div>
        ) : paymentStatus === 'pending' ? (
          <div className="text-center py-8">
            <Loader2 className="mx-auto mb-4 text-blue-500 animate-spin" size={64} />
            <h3 className="text-xl font-bold text-white mb-2">Paiement en cours...</h3>
            <p className="text-zinc-400">
              Vérifiez votre téléphone et confirmez le paiement de {amount}$
            </p>
            {paymentRef && (
              <p className="text-xs text-zinc-500 mt-2">Ref: {paymentRef}</p>
            )}
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div>
              <Label className="text-white mb-3 block">Choisir un opérateur</Label>
              <div className="grid grid-cols-1 gap-3">
                {operators.map(op => (
                  <button
                    key={op.value}
                    onClick={() => setSelectedOperator(op.value)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedOperator === op.value
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${op.color}`}></div>
                      <span className="font-semibold">{op.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="text-white">Numéro de téléphone</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+243..."
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white mt-2"
              />
            </div>

            <Button
              onClick={handlePayment}
              disabled={processing || !selectedOperator || !phoneNumber}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
            >
              {processing ? <Loader2 className="animate-spin" /> : `PAYER ${amount}$`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
